import { useMemo } from 'react';

import {
  useAiBannerFragment,
  useMemberAdminDataFragment,
  useWorkspaceAiData,
} from '@trello/atlassian-intelligence';
import { useMemberId } from '@trello/authentication';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { Entitlements } from '@trello/entitlements';
import { useFeatureGate } from '@trello/feature-gate-client';
import { getOrganizationAccountUrl } from '@trello/urls';

import { useBoardIdFromBoardOrCardRoute } from 'app/src/components/Board/useBoardIdFromBoardOrCardRoute';
import { useWorkspaceSettingsAtlassianIntelligenceFragment } from 'app/src/components/WorkspaceSettings/WorkspaceSettingsAtlassianIntelligenceFragment.generated';

export const AI_STANDARD_BANNER = `ai-standard-banner`;

/**
 * Custom hook to determine if the Personal Productivity Opt-In Banner should be rendered.
 *
 * This hook checks if the banner message has been dismissed, if the current route is a board or card route,
 * and if the feature gate for the banner is enabled.
 *
 * @returns {Object} An object containing a boolean `wouldRender`
 * (common interface in Banners.tsx)
 * indicating if the banner should be rendered.
 */

export function useAIStandardBanner(): {
  wouldRender: boolean;
  aiSettingsUrl?: string;
} {
  const boardId = useBoardIdFromBoardOrCardRoute() || '';

  const { data: board } = useAiBannerFragment({
    from: { id: boardId },
  });

  const { data: organization } =
    useWorkspaceSettingsAtlassianIntelligenceFragment({
      from: { id: board?.idOrganization },
    });

  // we want to show the workspace banner if the workspace is standard paid
  const isStandardPaidWorkspace = useMemo(() => {
    return Entitlements.isStandard(organization?.offering);
  }, [organization?.offering]);

  // Check if the board is eligible for the banner.
  const isBoardEligibleForBanner = useMemo(() => {
    return Boolean(
      board && board.prefs?.isTemplate === false && isStandardPaidWorkspace,
    );
  }, [board, isStandardPaidWorkspace]);

  // we need to check if the member is an admin of the board to determine if we should show the banner
  const memberId = useMemberId();

  const { data: member } = useMemberAdminDataFragment({
    from: { id: memberId },
  });

  const workspaceData = useWorkspaceAiData({
    boardId: board?.id!,
    member,
    skip: false,
  });

  const aiSettingsUrl = useMemo(() => {
    return workspaceData.board?.organization?.name
      ? getOrganizationAccountUrl(workspaceData.board?.organization?.id)
      : '';
  }, [
    workspaceData.board?.organization?.id,
    workspaceData.board?.organization?.name,
  ]);

  const { isWorkspaceAdmin } = useWorkspaceAiData({
    boardId,
    member,
    skip: !isBoardEligibleForBanner,
  });

  // Check if the banner was dismissed already
  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const isBannerDismissed = useMemo(() => {
    return isOneTimeMessageDismissed(`${AI_STANDARD_BANNER}`);
  }, [isOneTimeMessageDismissed]);

  //check if user is in the cohort
  const { value: isInCohort } = useFeatureGate(
    'goo_ai_eligibility_quickcapture_ga_check',
  );

  const wouldRender = useMemo(() => {
    return Boolean(
      !isBannerDismissed &&
        boardId &&
        isBoardEligibleForBanner &&
        isInCohort &&
        isWorkspaceAdmin,
    );
  }, [
    isBannerDismissed,
    boardId,
    isBoardEligibleForBanner,
    isInCohort,
    isWorkspaceAdmin,
  ]);

  return { wouldRender, aiSettingsUrl };
}
